/* Scoped navigation; Shopify still owns predictive search, accounts and cart. */
if (!customElements.get('blend-navigation')) {
  customElements.define('blend-navigation', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.triggers = [...this.querySelectorAll('[data-blend-panel]')];
      this.panels = [...this.querySelectorAll('[data-blend-menu]')];
      this.backdrop = this.querySelector('[data-blend-backdrop]');
      this.mobileTrigger = this.querySelector('[data-blend-mobile]');
      this.mobile = this.querySelector('[data-blend-mobile-dialog]');
      this.desktop = matchMedia('(min-width: 1000px)');
      this.actions = this.querySelector('.blend-header__actions');
      this.desktopActions ??= [...this.actions.children];
      this.updateMobileDock();
      this.timers = new Map();
      this.feature = this.querySelector('[data-blend-feature]');
      this.featureSlides = [...this.querySelectorAll('[data-blend-feature-slide]')];
      this.featureIndex = 0;
      this.featureRun = 0;
      this.sidebar = this.querySelector('[data-blend-sidebar]');
      this.sidebarSlides = [...this.querySelectorAll('[data-blend-sidebar-slide]')];
      this.sidebarIndex = 0;
      this.sidebarRun = 0;
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.feature?.addEventListener('pointerenter', () => {
        this.featureHovered = true;
        this.stopFeatureRotation();
      }, options);
      this.feature?.addEventListener('pointerleave', () => {
        this.featureHovered = false;
        this.scheduleFeatureRotation();
      }, options);
      this.feature?.addEventListener('focusin', () => this.stopFeatureRotation(), options);
      this.feature?.addEventListener('focusout', () => requestAnimationFrame(() => this.scheduleFeatureRotation()), options);
      this.sidebar?.addEventListener('pointerenter', () => {
        this.sidebarHovered = true;
        this.stopSidebarRotation();
      }, options);
      this.sidebar?.addEventListener('pointerleave', () => {
        this.sidebarHovered = false;
        this.scheduleSidebarRotation();
      }, options);
      this.sidebar?.addEventListener('focusin', () => this.stopSidebarRotation(), options);
      this.sidebar?.addEventListener('focusout', () => requestAnimationFrame(() => this.scheduleSidebarRotation()), options);
      this.reducedMotion.addEventListener('change', () => {
        this.scheduleFeatureRotation();
        this.scheduleSidebarRotation();
      }, options);
      document.addEventListener('visibilitychange', () => {
        this.scheduleFeatureRotation();
        this.scheduleSidebarRotation();
      }, options);
      this.addEventListener('click', event => {
        const trigger = event.target.closest('[data-blend-panel]');
        if (trigger) {
          const name = trigger.dataset.blendPanel;
          if (this.active === name) this.closePanel();
          else this.openPanel(name, event.detail === 0);
        }
        if (event.target.closest('[data-blend-close], [data-blend-backdrop]')) this.closePanel(true);
        if (event.target.closest('[data-blend-mobile]')) this.openMobile();
        if (event.target.closest('[data-blend-mobile-close]')) this.closeMobile();
        if (event.target.closest('a')) {
          // Close our navigation before the theme opens its search/cart drawer.
          this.closePanel();
          this.closeMobile(false);
        }
        const action = event.target.closest('.blend-header__actions > .blend-header__icon');
        if (action && !this.desktop.matches) this.setDockActive(action);
      }, options);
      document.addEventListener('dialog:after-hide', event => {
        if (['search-drawer', 'cart-drawer'].includes(event.target.id)) this.setDockActive();
      }, options);
      this.addEventListener('keydown', event => {
        const trigger = event.target.closest('[data-blend-panel]');
        if (trigger && event.key === 'ArrowDown') {
          event.preventDefault();
          this.openPanel(trigger.dataset.blendPanel, true);
        }
      }, options);
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape' && this.active) {
          event.preventDefault();
          this.closePanel(true);
        }
      }, options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.closePanel();
      }, options);
      document.addEventListener('focusin', event => {
        if (!this.active) return;
        const panel = this.panels.find(item => item.dataset.blendMenu === this.active);
        const trigger = this.triggers.find(item => item.dataset.blendPanel === this.active);
        if (!panel.contains(event.target) && event.target !== trigger) this.closePanel();
      }, options);
      this.mobile.addEventListener('cancel', event => {
        event.preventDefault();
        this.closeMobile();
      }, options);
      this.mobile.addEventListener('close', () => this.restoreMobileState(), options);
      this.mobile.addEventListener('click', event => {
        if (event.target !== this.mobile) return;
        const rect = this.mobile.getBoundingClientRect();
        if (event.clientY < rect.top || event.clientY > rect.bottom || event.clientX < rect.left || event.clientX > rect.right) this.closeMobile();
      }, options);
      this.desktop.addEventListener('change', () => {
        this.closePanel();
        this.closeMobile();
        this.updateMobileDock();
      }, options);
      this.updatePosition = () => {
        this.style.setProperty('--blend-header-bottom', `${Math.max(0, this.getBoundingClientRect().bottom)}px`);
        this.classList.toggle('is-scrolled', window.scrollY > 10);
      };
      window.addEventListener('scroll', this.updatePosition, { ...options, passive: true });
      window.addEventListener('resize', this.updatePosition, options);
      this.resizeObserver = new ResizeObserver(this.updatePosition);
      this.resizeObserver.observe(this);
      this.updatePosition();
    }
    openPanel(name, focus = false) {
      if (!this.desktop.matches) return;
      this.closePanel();
      const panel = this.panels.find(item => item.dataset.blendMenu === name);
      const trigger = this.triggers.find(item => item.dataset.blendPanel === name);
      if (!panel || !trigger) return;
      this.active = name;
      if (name === 'collections') {
        this.showSidebarSlide(0);
        this.sidebar?.querySelectorAll('img').forEach(image => { image.loading = 'eager'; });
      }
      clearTimeout(this.timers.get(panel));
      clearTimeout(this.timers.get(this.backdrop));
      panel.hidden = false;
      panel.inert = false;
      this.backdrop.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('has-menu');
      this.updatePosition();
      requestAnimationFrame(() => {
        if (this.active !== name) return;
        panel.classList.add('is-open');
        this.backdrop.classList.add('is-open');
        if (focus) panel.querySelector('a, button, summary')?.focus();
        this.scheduleFeatureRotation();
        this.scheduleSidebarRotation();
      });
    }
    closePanel(restoreFocus = false) {
      const name = this.active;
      this.active = null;
      this.stopFeatureRotation();
      this.stopSidebarRotation();
      this.classList.remove('has-menu');
      this.triggers.forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
      for (const element of [...this.panels, this.backdrop]) {
        if (element.hidden) continue;
        element.classList.remove('is-open');
        if (element !== this.backdrop) element.inert = true;
        clearTimeout(this.timers.get(element));
        const delay = matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 380;
        this.timers.set(element, setTimeout(() => { element.hidden = true; }, delay));
      }
      if (restoreFocus && name) this.triggers.find(item => item.dataset.blendPanel === name)?.focus();
    }
    updateMobileDock() {
      const mobileActions = ['mobile-trigger', 'search', 'wishlist', 'cart', 'account']
        .map(name => this.actions.querySelector(`.blend-header__${name}`)).filter(Boolean);
      this.actions.append(...(this.desktop.matches ? this.desktopActions : mobileActions));
      this.actions.style.setProperty('--blend-dock-items', mobileActions.length);
      this.setDockActive();
    }
    setDockActive(action) {
      if (!action) {
        const path = location.pathname;
        const name = /\/account(?:\/|$)/.test(path) ? 'account' : /\/cart\/?$/.test(path) ? 'cart' : /\/search\/?$/.test(path) ? 'search' : 'mobile-trigger';
        action = this.actions.querySelector(`.blend-header__${name}`);
      }
      this.actions.querySelectorAll('.blend-header__icon').forEach(icon => icon.classList.toggle('is-mobile-active', icon === action));
    }
    openMobile() {
      if (this.desktop.matches || this.mobile.open) return;
      this.closePanel();
      this.updatePosition();
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.mobileTrigger.setAttribute('aria-expanded', 'true');
      this.mobileTrigger.setAttribute('aria-label', 'Menüyü kapat');
      this.mobile.showModal();
    }
    closeMobile(restoreFocus = true) {
      if (!this.mobile.open) return;
      this.mobile.close();
      this.restoreMobileState();
      if (restoreFocus) this.mobileTrigger.focus();
    }
    restoreMobileState() {
      if (this.previousOverflow !== undefined) {
        document.body.style.overflow = this.previousOverflow;
        this.previousOverflow = undefined;
      }
      this.mobileTrigger.setAttribute('aria-expanded', 'false');
      this.mobileTrigger.setAttribute('aria-label', 'Menüyü aç');
      this.setDockActive();
    }
    canRotateFeature() {
      return this.isConnected && this.active === 'collections' && this.featureSlides.length > 1 &&
        !this.featureHovered && !this.reducedMotion.matches && !document.hidden &&
        !this.feature?.contains(document.activeElement);
    }
    stopFeatureRotation() {
      clearTimeout(this.featureTimer);
      this.featureTimer = null;
      this.featureRun++;
    }
    scheduleFeatureRotation() {
      this.stopFeatureRotation();
      if (!this.canRotateFeature()) return;
      this.featureTimer = setTimeout(() => this.advanceFeature(), 5000);
    }
    async advanceFeature() {
      const run = this.featureRun;
      if (!this.canRotateFeature()) return;
      const nextIndex = (this.featureIndex + 1) % this.featureSlides.length;
      const next = this.featureSlides[nextIndex];
      next.loading = 'eager';
      try {
        if (typeof next.decode === 'function') await next.decode();
        else if (!next.complete || !next.naturalWidth) { this.scheduleFeatureRotation(); return; }
      } catch {
        if (run === this.featureRun) this.scheduleFeatureRotation();
        return;
      }
      if (run !== this.featureRun || !this.canRotateFeature()) return;
      this.featureSlides[this.featureIndex].classList.remove('is-active');
      next.classList.add('is-active');
      this.featureIndex = nextIndex;
      this.scheduleFeatureRotation();
    }
    canRotateSidebar() {
      return this.isConnected && this.active === 'collections' && this.sidebarSlides.length > 1 &&
        Boolean(this.sidebar?.querySelector('.blend-concept-card')) && !this.sidebarHovered &&
        !this.reducedMotion.matches && !document.hidden && !this.sidebar.contains(document.activeElement);
    }
    showSidebarSlide(index) {
      this.sidebarSlides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.classList.toggle('is-active', active);
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
      });
      this.sidebarIndex = index;
    }
    stopSidebarRotation() {
      clearTimeout(this.sidebarTimer);
      this.sidebarTimer = null;
      this.sidebarRun++;
      this.sidebar?.classList.remove('is-timing');
    }
    scheduleSidebarRotation() {
      this.stopSidebarRotation();
      if (!this.canRotateSidebar()) return;
      // The "keşfet" underline fills over the same delay, so restart its animation with the timer.
      this.sidebar.style.setProperty('--blend-sidebar-delay', '5000ms');
      void this.sidebar.offsetWidth;
      this.sidebar.classList.add('is-timing');
      this.sidebarTimer = setTimeout(() => this.advanceSidebar(), 5000);
    }
    async advanceSidebar() {
      const run = this.sidebarRun;
      if (!this.canRotateSidebar()) return;
      const nextIndex = (this.sidebarIndex + 1) % this.sidebarSlides.length;
      const next = this.sidebarSlides[nextIndex];
      try {
        await Promise.all([...next.querySelectorAll('img')].map(image => {
          image.loading = 'eager';
          if (typeof image.decode === 'function') return image.decode();
          if (!image.complete || !image.naturalWidth) throw new Error('Image is still loading');
        }));
      } catch {
        if (run === this.sidebarRun) this.scheduleSidebarRotation();
        return;
      }
      if (run !== this.sidebarRun || !this.canRotateSidebar()) return;
      this.showSidebarSlide(nextIndex);
      this.scheduleSidebarRotation();
    }
    disconnectedCallback() {
      this.stopFeatureRotation();
      this.stopSidebarRotation();
      this.closeMobile(false);
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.timers?.forEach(clearTimeout);
    }
  });
}
